import { Box, Skeleton, Tooltip, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useMaterialUIController } from 'context';
import { getCrmColors } from '@layouts/pages/CRM/kanban-board/crm-theme';
import { ForecastReport } from '../services/responses/dashboard.response';

interface Props {
    data?: ForecastReport;
}

const money = (value: number) =>
    value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 });

/// Quanto os leads abertos prometem fechar, pela data prevista; não segue o período do filtro.
export const ForecastTile = ({ data }: Props) => {
    const { t, i18n } = useTranslation();
    const [controller] = useMaterialUIController();
    const cc = getCrmColors(controller.darkMode);

    const monthName = (year: number, month: number) =>
        new Date(year, month - 1, 1).toLocaleDateString(i18n.language, { month: 'long' });

    return (
        <Tooltip title={t('crmDashboard.previsaoAjuda')}>
            <Box
                sx={{
                    background: cc.cardBg,
                    border: `1px solid ${cc.border}`,
                    borderRadius: 3,
                    p: 2,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.75,
                }}
            >
                <Typography
                    variant="caption"
                    sx={{
                        color: cc.textSecondary,
                        textTransform: 'uppercase',
                        letterSpacing: '.08em',
                        fontWeight: 700,
                        fontSize: '10px',
                    }}
                >
                    {t('crmDashboard.previsao')}
                </Typography>

                {!data ? (
                    <Skeleton height={56} />
                ) : (
                    <>
                        {data.months.map((month) => (
                            <Box
                                key={`${month.year}-${month.month}`}
                                sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 1 }}
                            >
                                <Typography sx={{ fontSize: 12.5, color: cc.textSecondary, '&::first-letter': { textTransform: 'uppercase' } }}>
                                    {monthName(month.year, month.month)}
                                    <Box component="span" sx={{ color: cc.textMuted }}>
                                        {' '}· {t('crmDashboard.leadsQtd', { count: month.count })}
                                    </Box>
                                </Typography>
                                <Typography
                                    sx={{ fontSize: 16, fontWeight: 700, color: cc.textPrimary, fontVariantNumeric: 'tabular-nums' }}
                                >
                                    {money(month.total)}
                                </Typography>
                            </Box>
                        ))}

                        {data.overdueCount > 0 && (
                            <Typography sx={{ fontSize: 12, color: cc.eventDanger }}>
                                {t('crmDashboard.previsaoVencida', {
                                    total: data.overdueCount,
                                    valor: money(data.overdueTotal),
                                })}
                            </Typography>
                        )}
                    </>
                )}
            </Box>
        </Tooltip>
    );
};
